/**
 * Cart Puzzle Edit — JavaScript
 *
 * Handles edit functionality for puzzle photo magnets in the cart page.
 * Opens a modal showing the uploaded photo with grid overlay.
 * Supports Delete and Resize (crop) actions — no Duplicate.
 *
 * @version 1.0.0
 * @compatible Horizon Theme (Shopify Online Store 2.0)
 */

class CartPuzzleEdit {
    constructor() {
        this.file = null; // { dataUrl: string, id: string }
        this.editingItemKey = null;
        this.editingVariantId = null;
        this.gridCols = 3;
        this.gridRows = 3;
        this.cropRatio = 1;
        this.maxFileSizeMB = 10;
        this.allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        this.isSubmitting = false;

        this.rotation = 0;
        this.baseGridCols = 3;
        this.baseGridRows = 3;

        this.modal = null;

        // Bind
        this.handleEditClick = this.handleEditClick.bind(this);
        this.handleModalClose = this.handleModalClose.bind(this);
        this.handleUpdateCart = this.handleUpdateCart.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleRotate = this.handleRotate.bind(this);

        this.init();
    }

    init() {
        this.createModal();
        this.bindEditButtons();
        document.addEventListener('keydown', this.handleKeyDown);
    }

    createModal() {
        document.getElementById('cart-puzzle-edit-modal')?.remove();

        const modalHTML = `
    <div id="cart-puzzle-edit-modal" class="puzzle-upload-modal" role="dialog" aria-modal="true" aria-labelledby="cart-puzzle-edit-title">
      <div class="puzzle-upload-modal__container">
        <header class="puzzle-upload-modal__header">
          <h2 class="puzzle-upload-modal__title" id="cart-puzzle-edit-title">Edit Puzzle Photo</h2>
          <button type="button" class="puzzle-upload-modal__close" aria-label="Close">&times;</button>
        </header>

        <div class="puzzle-upload-modal__body">
          <div class="puzzle-upload-preview" style="min-height: 0;">
            <div class="puzzle-upload-preview__grid-wrapper">
              <div class="puzzle-upload-preview__image-container" data-testid="cart-puzzle-image-container">
                <!-- Image + grid overlay rendered by JS -->
              </div>

              <div class="puzzle-upload-preview__sidebar">
                <div class="puzzle-upload-preview__info">
                  <h3>Product Preview</h3>
                  <div class="puzzle-upload-preview__note">
                    <span class="puzzle-upload-preview__note-icon"></span>
                    <span>The grid shows how your photo will be split. Each cell represents one photo magnet you will receive.</span>
                  </div>
                  <p class="puzzle-upload-preview__tip">You will receive:</p>
                  <p class="puzzle-upload-preview__variant-label" data-variant-label>
                    <strong data-grid-label>3×3</strong> = <span data-magnet-count>9</span> photo magnets
                  </p>
                </div>

                <div class="puzzle-upload-sidebar-buttons">
                  <div class="puzzle-upload-preview__actions">
                    <button type="button" class="puzzle-upload-action-btn puzzle-upload-action-btn--change" data-action="change-picture" title="Change picture">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                      </svg>
                      <span>Change picture</span>
                    </button>
                    <input type="file" class="puzzle-upload-change-input" accept="image/jpeg,image/png,image/webp" style="display:none;">
                    <button type="button" class="puzzle-upload-action-btn puzzle-upload-action-btn--resize" data-action="resize" title="Resize/crop photo">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                        <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15"/>
                        <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15"/>
                      </svg>
                      <span>Resize</span>
                    </button>
                    <button type="button" class="puzzle-upload-action-btn puzzle-upload-action-btn--rotate" data-action="rotate" title="Rotate 90°">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                        <path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                      </svg>
                      <span>Rotate</span>
                    </button>
                  </div>
                  <button type="button" class="puzzle-upload-addtocart-btn" data-testid="cart-puzzle-save">
                    SAVE CHANGES
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>`;

        document.body.insertAdjacentHTML('beforeend', modalHTML);

        this.modal = document.getElementById('cart-puzzle-edit-modal');
        this.imageContainer = this.modal.querySelector('[data-testid="cart-puzzle-image-container"]');

        // Bind modal events
        this.modal.querySelector('.puzzle-upload-modal__close')?.addEventListener('click', this.handleModalClose);
        this.modal.querySelector('.puzzle-upload-addtocart-btn')?.addEventListener('click', this.handleUpdateCart);
        this.modal.querySelector('[data-action="change-picture"]')?.addEventListener('click', () => this.handleChangePicture());
        this.changeInput = this.modal.querySelector('.puzzle-upload-change-input');
        this.changeInput?.addEventListener('change', (e) => this.handleChangeFile(e));
        this.modal.querySelector('[data-action="resize"]')?.addEventListener('click', () => this.openResizeModal());
        this.modal.querySelector('[data-action="rotate"]')?.addEventListener('click', this.handleRotate);
        this.modal.addEventListener('click', (e) => {
            if (e.target === this.modal) this.handleModalClose();
        });
    }

    bindEditButtons() {
        document.querySelectorAll('[data-action="edit-puzzle"]').forEach(btn => {
            btn.addEventListener('click', this.handleEditClick);
        });

        // Observe for dynamically added buttons
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === 1) {
                        node.querySelectorAll?.('[data-action="edit-puzzle"]')?.forEach(btn => {
                            btn.removeEventListener('click', this.handleEditClick);
                            btn.addEventListener('click', this.handleEditClick);
                        });
                    }
                });
            });
        });
        observer.observe(document.body, { childList: true, subtree: true });
    }

    handleEditClick(e) {
        const button = e.currentTarget;
        this.editingItemKey = button.dataset.itemKey;
        this.editingVariantId = button.dataset.variantId;

        // Parse grid
        const grid = button.dataset.puzzleGrid || '3x3';
        const match = grid.match(/(\d+)\s*[x×X]\s*(\d+)/);
        if (match) {
            this.gridCols = parseInt(match[1], 10);
            this.gridRows = parseInt(match[2], 10);
            this.baseGridCols = this.gridCols;
            this.baseGridRows = this.gridRows;
            this.cropRatio = this.gridCols / this.gridRows;
        }
        this.rotation = 0;

        // Load photo
        const photoUrl = decodeURIComponent(button.dataset.puzzlePhoto || '');
        if (photoUrl) {
            this.file = {
                dataUrl: photoUrl,
                id: 'edit_' + Math.random().toString(36).substr(2, 5)
            };
        }

        // Update display
        const gridLabel = this.modal.querySelector('[data-grid-label]');
        const magnetCount = this.modal.querySelector('[data-magnet-count]');
        if (gridLabel) gridLabel.textContent = `${this.gridCols}×${this.gridRows}`;
        if (magnetCount) magnetCount.textContent = this.gridCols * this.gridRows;

        this.openModal();
    }

    openModal() {
        this.modal?.classList.add('active');
        document.body.style.overflow = 'hidden';
        this.updatePreview();
    }

    handleModalClose() {
        this.modal?.classList.remove('active');
        document.body.style.overflow = '';
        this.file = null;
        this.editingItemKey = null;
        this.isSubmitting = false;
    }

    handleKeyDown(e) {
        if (e.key === 'Escape' && this.modal?.classList.contains('active')) {
            this.handleModalClose();
        }
    }

    handleChangePicture() {
        this.changeInput?.click();
    }

    async handleChangeFile(e) {
        const file = e.target?.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            this.file = {
                dataUrl: reader.result,
                rawFile: file,
                id: 'change_' + Math.random().toString(36).substr(2, 5)
            };
            this.updatePreview();
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    }

    updatePreview() {
        if (!this.imageContainer || !this.file) return;

        this.imageContainer.innerHTML = `
      <div class="puzzle-preview__wrapper" style="aspect-ratio: ${this.gridCols} / ${this.gridRows};">
        <img class="puzzle-preview__image" src="${this.file.dataUrl}" alt="Puzzle preview" />
        <div class="puzzle-grid-overlay" style="grid-template-columns: repeat(${this.gridCols}, 1fr); grid-template-rows: repeat(${this.gridRows}, 1fr);">
          ${this.renderGridCells()}
        </div>
      </div>
    `;
    }

    renderGridCells() {
        const total = this.gridCols * this.gridRows;
        let html = '';
        for (let i = 0; i < total; i++) {
            html += `<div class="puzzle-grid-overlay__cell"><span class="puzzle-grid-overlay__number">${i + 1}</span></div>`;
        }
        return html;
    }

    async handleRotate() {
        if (!this.file || !this.file.dataUrl) return;

        this.rotation = (this.rotation + 90) % 360;

        try {
            // 1. Rotate the image pixels
            const rotatedDataUrl = await this.rotateImage(this.file.dataUrl, 90);

            // 2. Swap grid dimensions for 90/270 degrees
            if (this.rotation % 180 !== 0) {
                this.gridCols = this.baseGridRows;
                this.gridRows = this.baseGridCols;
            } else {
                this.gridCols = this.baseGridCols;
                this.gridRows = this.baseGridRows;
            }
            this.cropRatio = this.gridCols / this.gridRows;

            // 3. Update state
            this.file.dataUrl = rotatedDataUrl;

            // 4. Update UI labels
            const gridLabel = this.modal.querySelector('[data-grid-label]');
            const magnetCount = this.modal.querySelector('[data-magnet-count]');
            if (gridLabel) gridLabel.textContent = `${this.gridCols}×${this.gridRows}`;
            if (magnetCount) magnetCount.textContent = this.gridCols * this.gridRows;

            this.updatePreview();
        } catch (err) {
            console.error('Rotation failed:', err);
        }
    }

    rotateImage(dataUrl, degrees) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // Swap width/height for 90 or 270 degree rotation
                if (degrees % 180 !== 0) {
                    canvas.width = img.height;
                    canvas.height = img.width;
                } else {
                    canvas.width = img.width;
                    canvas.height = img.height;
                }

                if (!ctx) {
                    reject(new Error('Failed to get canvas context'));
                    return;
                }

                ctx.translate(canvas.width / 2, canvas.height / 2);
                ctx.rotate((degrees * Math.PI) / 180);
                ctx.drawImage(img, -img.width / 2, -img.height / 2);

                resolve(canvas.toDataURL('image/jpeg', 0.95));
            };
            img.onerror = reject;
            img.src = dataUrl;
        });
    }

    // ========================================
    // RESIZE MODAL (same pattern as puzzle-upload.js)
    // ========================================

    openResizeModal() {
        if (!this.file) return;

        const cropModal = document.createElement('div');
        cropModal.className = 'puzzle-upload-crop-modal';
        cropModal.innerHTML = `
      <div class="puzzle-upload-crop-modal__overlay"></div>
      <div class="puzzle-upload-crop-modal__container">
        <div class="puzzle-upload-crop-modal__header">
          <h3>Resize Image</h3>
          <button type="button" class="puzzle-upload-crop-modal__close">&times;</button>
        </div>
        <div class="puzzle-upload-crop-modal__body">
          <div class="puzzle-upload-crop-modal__canvas-wrapper">
            <canvas class="puzzle-upload-crop-modal__canvas"></canvas>
            <div class="puzzle-upload-crop-modal__crop-area">
              <div class="puzzle-upload-crop-modal__resize-handle puzzle-upload-crop-modal__resize-handle--nw" data-handle="nw"></div>
              <div class="puzzle-upload-crop-modal__resize-handle puzzle-upload-crop-modal__resize-handle--ne" data-handle="ne"></div>
              <div class="puzzle-upload-crop-modal__resize-handle puzzle-upload-crop-modal__resize-handle--sw" data-handle="sw"></div>
              <div class="puzzle-upload-crop-modal__resize-handle puzzle-upload-crop-modal__resize-handle--se" data-handle="se"></div>
            </div>
          </div>
          <div class="puzzle-upload-crop-modal__toolbar">
            <button type="button" class="puzzle-upload-crop-modal__tool-btn" data-tool="flip-h" title="Flip Horizontal">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
                <path d="M16 3l4 4-4 4"/><path d="M20 7H4"/>
                <path d="M8 21l-4-4 4-4"/><path d="M4 17h16"/>
              </svg>
              <span>Flip H</span>
            </button>
            <button type="button" class="puzzle-upload-crop-modal__tool-btn" data-tool="flip-v" title="Flip Vertical">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
                <path d="M3 8l4-4 4 4"/><path d="M7 4v16"/>
                <path d="M21 16l-4 4-4-4"/><path d="M17 20V4"/>
              </svg>
              <span>Flip V</span>
            </button>
            <button type="button" class="puzzle-upload-crop-modal__tool-btn" data-tool="rotate-cw" title="Rotate 90°">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
                <path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
              <span>Rotate</span>
            </button>
          </div>
        </div>
        <div class="puzzle-upload-crop-modal__footer">
          <button type="button" class="puzzle-upload-crop-modal__cancel">Cancel</button>
          <button type="button" class="puzzle-upload-crop-modal__apply">Apply</button>
        </div>
      </div>
    `;

        document.body.appendChild(cropModal);

        let flipH = false;
        let flipV = false;
        let rotation = 0;
        const ratio = this.cropRatio;

        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
            const canvas = cropModal.querySelector('.puzzle-upload-crop-modal__canvas');
            const ctx = canvas.getContext('2d');
            const wrapper = cropModal.querySelector('.puzzle-upload-crop-modal__canvas-wrapper');
            const cropArea = cropModal.querySelector('.puzzle-upload-crop-modal__crop-area');

            const isMobile = window.innerWidth <= 767;
            const maxSize = isMobile ? 400 : 600;

            const getDisplayDims = () => {
                const isRotated = rotation === 90 || rotation === 270;
                const srcW = isRotated ? img.height : img.width;
                const srcH = isRotated ? img.width : img.height;
                let dw = srcW, dh = srcH;
                if (dw > maxSize || dh > maxSize) {
                    const r = Math.min(maxSize / dw, maxSize / dh);
                    dw = Math.round(srcW * r);
                    dh = Math.round(srcH * r);
                }
                return { displayWidth: dw, displayHeight: dh };
            };

            let cropX, cropY, cropW, cropH;
            const minCropDim = 50;

            const initCropArea = (dw, dh) => {
                if (dw / dh > ratio) {
                    cropH = dh;
                    cropW = Math.round(dh * ratio);
                } else {
                    cropW = dw;
                    cropH = Math.round(dw / ratio);
                }
                cropX = Math.round((dw - cropW) / 2);
                cropY = Math.round((dh - cropH) / 2);
                updateCropAreaUI();
            };

            const updateCropAreaUI = () => {
                cropArea.style.width = cropW + 'px';
                cropArea.style.height = cropH + 'px';
                cropArea.style.left = cropX + 'px';
                cropArea.style.top = cropY + 'px';
            };

            const redrawCanvas = () => {
                const { displayWidth, displayHeight } = getDisplayDims();
                canvas.width = displayWidth;
                canvas.height = displayHeight;
                wrapper.style.width = displayWidth + 'px';
                wrapper.style.height = displayHeight + 'px';

                ctx.save();
                ctx.clearRect(0, 0, displayWidth, displayHeight);
                ctx.translate(displayWidth / 2, displayHeight / 2);
                ctx.rotate((rotation * Math.PI) / 180);
                ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);

                const isRotated = rotation === 90 || rotation === 270;
                const drawW = isRotated ? displayHeight : displayWidth;
                const drawH = isRotated ? displayWidth : displayHeight;
                ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
                ctx.restore();

                initCropArea(displayWidth, displayHeight);
            };

            // Initial draw
            let { displayWidth, displayHeight } = getDisplayDims();
            canvas.width = displayWidth;
            canvas.height = displayHeight;
            wrapper.style.width = displayWidth + 'px';
            wrapper.style.height = displayHeight + 'px';
            ctx.drawImage(img, 0, 0, displayWidth, displayHeight);
            initCropArea(displayWidth, displayHeight);

            // Toolbar
            cropModal.querySelector('[data-tool="flip-h"]').addEventListener('click', () => { flipH = !flipH; redrawCanvas(); });
            cropModal.querySelector('[data-tool="flip-v"]').addEventListener('click', () => { flipV = !flipV; redrawCanvas(); });
            cropModal.querySelector('[data-tool="rotate-cw"]').addEventListener('click', () => {
                rotation = (rotation + 90) % 360;
                redrawCanvas();
                const dims = getDisplayDims();
                displayWidth = dims.displayWidth;
                displayHeight = dims.displayHeight;
            });

            // Drag and resize
            let isDragging = false, isResizing = false, resizeHandle = null;
            let startX = 0, startY = 0, startCropX = 0, startCropY = 0, startCropW = 0, startCropH = 0;

            const handleCropAreaDown = (e) => {
                const point = e.touches ? e.touches[0] : e;
                if (e.target.dataset.handle) return;
                isDragging = true;
                startX = point.clientX; startY = point.clientY;
                startCropX = cropX; startCropY = cropY;
                e.preventDefault();
            };
            cropArea.addEventListener('mousedown', handleCropAreaDown);
            cropArea.addEventListener('touchstart', handleCropAreaDown, { passive: false });

            cropModal.querySelectorAll('.puzzle-upload-crop-modal__resize-handle').forEach(handle => {
                const handleDown = (e) => {
                    const point = e.touches ? e.touches[0] : e;
                    isResizing = true;
                    resizeHandle = (e.target || e.currentTarget).dataset.handle;
                    startX = point.clientX; startY = point.clientY;
                    startCropX = cropX; startCropY = cropY;
                    startCropW = cropW; startCropH = cropH;
                    e.preventDefault(); e.stopPropagation();
                };
                handle.addEventListener('mousedown', handleDown);
                handle.addEventListener('touchstart', handleDown, { passive: false });
            });

            const handlePointerMove = (e) => {
                const point = e.touches ? e.touches[0] : e;
                const curDims = getDisplayDims();
                const dw = curDims.displayWidth, dh = curDims.displayHeight;

                if (isDragging) {
                    const dx = point.clientX - startX, dy = point.clientY - startY;
                    cropX = Math.max(0, Math.min(dw - cropW, startCropX + dx));
                    cropY = Math.max(0, Math.min(dh - cropH, startCropY + dy));
                    updateCropAreaUI();
                } else if (isResizing) {
                    const dx = point.clientX - startX, dy = point.clientY - startY;
                    const delta = Math.abs(dx) > Math.abs(dy) ? dx : dy;
                    let newW, newH, newX, newY;
                    switch (resizeHandle) {
                        case 'se': newW = Math.max(minCropDim, startCropW + delta); newH = Math.round(newW / ratio); newX = startCropX; newY = startCropY; break;
                        case 'sw': newW = Math.max(minCropDim, startCropW - dx); newH = Math.round(newW / ratio); newX = startCropX + startCropW - newW; newY = startCropY; break;
                        case 'ne': newW = Math.max(minCropDim, startCropW + dx); newH = Math.round(newW / ratio); newX = startCropX; newY = startCropY + startCropH - newH; break;
                        case 'nw': newW = Math.max(minCropDim, startCropW - delta); newH = Math.round(newW / ratio); newX = startCropX + startCropW - newW; newY = startCropY + startCropH - newH; break;
                        default: return;
                    }
                    if (newX >= 0 && newY >= 0 && newX + newW <= dw && newY + newH <= dh && newW >= minCropDim && newH >= minCropDim) {
                        cropW = newW; cropH = newH; cropX = newX; cropY = newY;
                        updateCropAreaUI();
                    }
                }
            };

            const handlePointerUp = () => { isDragging = false; isResizing = false; resizeHandle = null; };

            document.addEventListener('mousemove', handlePointerMove);
            document.addEventListener('mouseup', handlePointerUp);
            document.addEventListener('touchmove', handlePointerMove, { passive: false });
            document.addEventListener('touchend', handlePointerUp);

            // Apply
            cropModal.querySelector('.puzzle-upload-crop-modal__apply').addEventListener('click', () => {
                const curDims = getDisplayDims();
                const dw = curDims.displayWidth, dh = curDims.displayHeight;

                const isRotated = rotation === 90 || rotation === 270;
                const fullW = isRotated ? img.height : img.width;
                const fullH = isRotated ? img.width : img.height;

                const transformCanvas = document.createElement('canvas');
                transformCanvas.width = fullW; transformCanvas.height = fullH;
                const tCtx = transformCanvas.getContext('2d');
                tCtx.translate(fullW / 2, fullH / 2);
                tCtx.rotate((rotation * Math.PI) / 180);
                tCtx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
                const drawW = isRotated ? fullH : fullW;
                const drawH = isRotated ? fullW : fullH;
                tCtx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);

                const scaleX = fullW / dw, scaleY = fullH / dh;
                const actualX = Math.round(cropX * scaleX);
                const actualY = Math.round(cropY * scaleY);
                const actualW = Math.round(cropW * scaleX);
                const actualH = Math.round(cropH * scaleY);

                const cropCanvas = document.createElement('canvas');
                cropCanvas.width = actualW; cropCanvas.height = actualH;
                const cropCtx = cropCanvas.getContext('2d');
                cropCtx.drawImage(transformCanvas, actualX, actualY, actualW, actualH, 0, 0, actualW, actualH);

                const tempImg = new Image();
                tempImg.onload = () => {
                    this.file.dataUrl = this.compressImage(tempImg, 1200, 0.85);
                    cleanup();
                    this.updatePreview();
                };
                tempImg.src = cropCanvas.toDataURL('image/jpeg', 0.9);
            });

            const cleanup = () => {
                document.removeEventListener('mousemove', handlePointerMove);
                document.removeEventListener('mouseup', handlePointerUp);
                document.removeEventListener('touchmove', handlePointerMove);
                document.removeEventListener('touchend', handlePointerUp);
                cropModal.remove();
            };

            cropModal.querySelector('.puzzle-upload-crop-modal__close').addEventListener('click', cleanup);
            cropModal.querySelector('.puzzle-upload-crop-modal__cancel').addEventListener('click', cleanup);
            cropModal.querySelector('.puzzle-upload-crop-modal__overlay').addEventListener('click', cleanup);
        };

        img.src = this.file.dataUrl;
    }

    // ========================================
    // UTILITIES
    // ========================================

    compressImage(img, maxSize, quality) {
        let width = img.width, height = img.height;
        if (width > maxSize || height > maxSize) {
            const r = Math.min(maxSize / width, maxSize / height);
            width = Math.round(width * r);
            height = Math.round(height * r);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        return canvas.toDataURL('image/jpeg', quality);
    }

    compressForCart(dataUrl) {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(this.compressImage(img, 400, 0.6));
            img.onerror = () => resolve(dataUrl);
            img.src = dataUrl;
        });
    }

    renderPuzzlePreviewImage(size, quality) {
        size = size || 400;
        quality = quality || 0.7;
        return new Promise((resolve) => {
            if (!this.file) { resolve(null); return; }
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                const w = Math.round(size);
                const h = Math.round(size / this.cropRatio);
                const canvas = document.createElement('canvas');
                canvas.width = w; canvas.height = h;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, w, h);

                ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
                ctx.lineWidth = size > 600 ? 3 : 2;
                ctx.setLineDash([6, 4]);
                for (let i = 1; i < this.gridCols; i++) {
                    const x = Math.round((i / this.gridCols) * w);
                    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
                }
                for (let i = 1; i < this.gridRows; i++) {
                    const y = Math.round((i / this.gridRows) * h);
                    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
                }
                resolve(canvas.toDataURL('image/jpeg', quality));
            };
            img.onerror = () => resolve(null);
            img.src = this.file.dataUrl;
        });
    }

    // ========================================
    // CLOUDFLARE WORKER UPLOAD
    // ========================================

    getWorkerConfig() {
        // Try to find config from puzzle-upload-component on the page
        const puzzleComponent = document.querySelector('puzzle-upload-component');
        if (puzzleComponent) {
            const url = puzzleComponent.dataset?.workerUrl?.trim();
            const key = puzzleComponent.dataset?.workerKey?.trim();
            if (url) return { url: url.replace(/\/$/, ''), key: key || '' };
        }
        // Try data attributes on body or script tag
        const configEl = document.querySelector('[data-puzzle-worker-url]');
        if (configEl) {
            const url = configEl.dataset.puzzleWorkerUrl?.trim();
            if (url) return { url: url.replace(/\/$/, ''), key: configEl.dataset.puzzleWorkerKey || '' };
        }
        return null;
    }

    dataUrlToBlob(dataUrl) {
        const [header, base64Data] = dataUrl.split(',');
        const mimeMatch = header.match(/:(.*?);/);
        const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
        const binary = atob(base64Data);
        const array = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            array[i] = binary.charCodeAt(i);
        }
        return new Blob([array], { type: mime });
    }

    async uploadToWorker(fileOrBlob, filename, config) {
        try {
            const formData = new FormData();
            formData.append('file', fileOrBlob, filename);
            const response = await fetch(`${config.url}/upload`, {
                method: 'POST',
                headers: { 'X-API-Key': config.key },
                body: formData
            });
            if (!response.ok) return null;
            const result = await response.json();
            return result.url || null;
        } catch (err) {
            console.warn('[CartPuzzleEdit] Worker upload failed:', err.message);
            return null;
        }
    }

    // ========================================
    // SAVE / UPDATE CART
    // ========================================

    async handleUpdateCart() {
        if (this.isSubmitting) return;
        this.isSubmitting = true;

        const submitBtn = this.modal.querySelector('.puzzle-upload-addtocart-btn');
        const originalText = submitBtn.textContent;
        submitBtn.textContent = 'Saving...';
        submitBtn.disabled = true;

        try {
            // Get existing cart item properties to preserve non-puzzle ones
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();
            const existingItem = cart.items.find(item => item.key === this.editingItemKey);

            const properties = {
                '_puzzle_grid': `${this.gridCols}x${this.gridRows}`,
                '_puzzle_count': (this.gridCols * this.gridRows).toString(),
                '_upload_id': Date.now().toString()
            };

            // Preserve non-puzzle properties (except old photo/puzzle keys)
            if (existingItem?.properties) {
                for (const [key, value] of Object.entries(existingItem.properties)) {
                    if (key.includes('Puzzle') || key.includes('puzzle') || key.includes('Photo') || key === '_upload_id') continue;
                    properties[key] = value;
                }
            }

            // Photo data — upload to R2 if Worker config available
            if (this.file) {
                const workerConfig = this.getWorkerConfig();
                if (!workerConfig) throw new Error('Image upload service is not configured.');

                // === UPLOAD 1: Cropped puzzle image to R2 ===
                submitBtn.textContent = 'Uploading...';
                if (this.file.dataUrl.startsWith('https://')) {
                    properties['Puzzle Image'] = this.file.dataUrl;
                } else {
                    const croppedBlob = this.dataUrlToBlob(this.file.dataUrl);
                    const croppedUrl = await this.uploadToWorker(croppedBlob, 'puzzle-edit-cropped.jpg', workerConfig);
                    if (!croppedUrl) throw new Error('Failed to upload photo. Please try again.');
                    properties['Puzzle Image'] = croppedUrl;
                }

                // Preserve existing Original Photo URL, or upload new one if file was changed
                if (this.file.rawFile) {
                    submitBtn.textContent = 'Uploading original...';
                    const origBlob = this.file.rawFile;
                    const origUrl = await this.uploadToWorker(origBlob, origBlob.name || 'puzzle-original.jpg', workerConfig);
                    if (origUrl) properties['Original Photo'] = origUrl;
                } else if (existingItem?.properties?.['Original Photo']) {
                    properties['Original Photo'] = existingItem.properties['Original Photo'];
                }

                // === UPLOAD 2: Grid overlay composite to R2 ===
                submitBtn.textContent = 'Creating preview...';
                const previewDataUrl = await this.renderPuzzlePreviewImage(1200, 0.9);
                if (previewDataUrl) {
                    const previewBlob = this.dataUrlToBlob(previewDataUrl);
                    const previewUrl = await this.uploadToWorker(previewBlob, 'puzzle-edit-grid-preview.jpg', workerConfig);
                    if (!previewUrl) throw new Error('Failed to upload preview. Please try again.');
                    properties['Puzzle Preview'] = previewUrl;
                }
            }

            submitBtn.textContent = 'Updating cart...';

            // Remove old item, add new
            await this.updateCartItem(this.editingItemKey, 0);
            const addResult = await this.addToCart(this.editingVariantId, 1, properties);

            if (addResult.status && addResult.status >= 400) {
                throw new Error(addResult.description || addResult.message || 'Failed to update');
            }

            this.handleModalClose();
            window.location.reload();

        } catch (error) {
            console.error('[CartPuzzleEdit] Error:', error);
            alert('Failed to update cart. Please try again.');
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
            this.isSubmitting = false;
        }
    }

    updateCartItem(key, quantity) {
        return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity })
        }).then(res => res.json());
    }

    addToCart(variantId, quantity, properties) {
        return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: parseInt(variantId), quantity, properties })
        }).then(res => res.json());
    }
}

// Initialize
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { window.cartPuzzleEdit = new CartPuzzleEdit(); });
} else {
    window.cartPuzzleEdit = new CartPuzzleEdit();
}
